import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { OverlayTrigger, Popover, Dropdown } from 'react-bootstrap'
import { Flex } from 'reflexbox'
import BreadCrumb from 'ui-components/lib/components/BreadCrumb'
import Modal from 'ui-components/lib/components/Modal'
import TextField from 'ui-components/lib/components/TextField'
import Spinner from 'ui-components/lib/components/Spinner'
import { inpiQueries, clientsQueries } from '../../graphql/queries'
import { inpiMutations } from '../../graphql/mutations'
import useAlertStack from 'ui-components/lib/hooks/useAlertStack'
import {
  Tag,
  Code,
  Lightbulb,
  Plus,
  Bell,
  Users,
  Search,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileText,
  Clock,
  List,
  Shield,
  ExternalLink,
  Check,
  Globe
} from 'lucide-react'
import { StatusBadge, statusConfig, normalizeStatus } from './StatusBadge'
import TipoIcon, { tipoConfig } from './TipoIcon'
import { useTheme } from '../../contexts/ThemeContext'
import './RegistrosInpi.css'

const PROCESS_TYPES = [
  { value: 'marca', label: 'Marca' },
  { value: 'patente', label: 'Patente' },
  { value: 'software', label: 'Registro de software' }
]

const ACOES_SOLICITACAO = [
  { tipo: 'marca', label: 'Registrar Marca', descricao: 'Nacional — Classes INPI', icon: Tag },
  { tipo: 'software', label: 'Registro de Software', descricao: 'Programa de computador', icon: Code },
  { tipo: 'marca_internacional', label: 'Marca Internacional', descricao: 'Protocolo de Madrid', icon: Globe },
  { tipo: 'patente', label: 'Patente', descricao: 'Invenção ou modelo de utilidade', icon: Lightbulb }
]

const OPCOES_REGISTRAR_MARCA = [
  { label: 'Registrar nova Marca', subserviceId: '653ab7aff44a0500085abbe4' },
  { label: 'Acompanhar registro de marca', subserviceId: '653c1111b256e500085228ab' },
  { label: 'Transferir marca', subserviceId: '653abc8b0c1e7200089a4cab' }
]

const STATUS_OPTIONS = [
  { value: 'todos', label: 'Todos os status' },
  { value: 'pedido_protocolado', label: 'Pedido protocolado' },
  { value: 'exame_formal', label: 'Exame formal' },
  { value: 'aguardando_oposicao', label: 'Aguard. oposição' },
  { value: 'aguardando_manifestacao', label: 'Aguard. manifestação' },
  { value: 'aguardando_merito', label: 'Aguard. mérito' },
  { value: 'deferido', label: 'Deferido' },
  { value: 'indeferido', label: 'Indeferido' },
  { value: 'registro_ativo', label: 'Registro ativo' },
  { value: 'arquivado', label: 'Arquivado' },
  { value: 'pedido_inexistente', label: 'Pedido inexistente' }
]

const TIPO_OPTIONS = [
  { value: 'todos', label: 'Todos os tipos' },
  { value: 'marca', label: 'Registro de Marca' },
  { value: 'software', label: 'Registro de Software' },
  { value: 'marca_internacional', label: 'Marca Internacional' },
  { value: 'patente', label: 'Patente' }
]

function formatDateBR(value) {
  if (!value) return value
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value
}

function formatProcessNumberBR(num) {
  if (!num) return num
  const n = String(num).replace(/\D/g, '')
  if (n.length < 13) return num
  return `BR ${n.slice(0, 2)} ${n.slice(2, 6)} ${n.slice(6, 12)} ${n.slice(12)}`
}


function formatComplemento(text) {
  if (!text || typeof text !== 'string') return text
  return text
    .replace(/(Protocolo|Petição\s*\(tipo\)|Petição|Requerente|Procurador|Detalhes do despacho)\s*:/gi, '\n$1: ')
    .replace(/^\s*\n+/, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n')
    .trim()
}

function mapProcessToRegistro(proc) {
  const tipo = proc.processType || 'marca'
  const statusKey = normalizeStatus(proc.status)
  const lastMov = proc.lastMovement
  const ultimaMovimentacao = lastMov
    ? `${lastMov.description || ''}${lastMov.date ? ` — ${formatDateBR(lastMov.date)}` : ''}`.trim() : 'Sem movimentação';

  const dataAbertura = proc.depositDate || proc.publicationDate
  let descricao = proc.statusDetail || '—'
  if (descricao === '—' && tipo === 'software') descricao = 'Programa de computador'
  if (descricao === '—' && tipo === 'patente') descricao = 'Patente'
  if (descricao === '—' && proc.classNumbers && proc.classNumbers.length) descricao = `Classes: ${proc.classNumbers.join(', ')}`

  return {
    id: proc._id,
    nome: proc.trademarkName || '—',
    protocolo: proc.processNumber || '—',
    numeroServico: null,
    tipo: (tipo === 'marca_internacional' ? 'marca' : tipoConfig[tipo] ? tipo : 'outro'),
    status: statusKey,
    ultimaMovimentacao: ultimaMovimentacao,
    dataAbertura: dataAbertura ? formatDateBR(dataAbertura) : '—',
    descricao,
    raw: proc
  }
}

const RegistrosInpi = () => {
  const history = useHistory()
  const { isDark } = useTheme()
  const [busca, setBusca] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('todos')
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [dialogNovoAberto, setDialogNovoAberto] = useState(false)
  const [dialogEquipeAberto, setDialogEquipeAberto] = useState(false)
  const [dialogDetalheAberto, setDialogDetalheAberto] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [preSelectedTipo, setPreSelectedTipo] = useState(null)
  const [marcaDropdownOpen, setMarcaDropdownOpen] = useState(false)
  const [teamMembers, setTeamMembers] = useState([])
  const [buscaEquipe, setBuscaEquipe] = useState('')
  const hasInitializedEquipeModal = useRef(false)
  const { showMessage } = useAlertStack()
  const [selectedRegistro, setSelectedRegistro] = useState(null)
  const [processNumber, setProcessNumber] = useState('')
  const [processType, setProcessType] = useState('marca')
  const [addError, setAddError] = useState('')
  const [detalheSections, setDetalheSections] = useState({
    classificacao: true,
    peticoes: true,
    publicacoes: true
  })

  const { loading, data, refetch } = useQuery(inpiQueries.GET_INPI_PROCESSES)
  const { data: processDetail, loading: loadingDetail } = useQuery(inpiQueries.GET_INPI_PROCESS, {
    variables: { id: selectedRegistro?.id },
    skip: !selectedRegistro?.id || !dialogDetalheAberto
  })

  const { data: dataAccountUsers } = useQuery(clientsQueries.GET_ACCOUNT_USERS)
  const { data: recipientsData, refetch: refetchRecipients } = useQuery(inpiQueries.GET_INPI_NOTIFICATION_RECIPIENTS)

  const baseTeamList = useMemo(() => {
    const users = dataAccountUsers?.getAccountUsers || []
    return users.map((u) => ({
      id: u._id,
      nome: u.name || '—',
      email: u.email || '',
      ativo: false
    }))
  }, [dataAccountUsers?.getAccountUsers])

  const prevEquipeOpen = useRef(false)
  useEffect(() => {
    if (!dialogEquipeAberto) {
      hasInitializedEquipeModal.current = false
      prevEquipeOpen.current = false
      return
    }
    if (!baseTeamList.length) return
    const justOpened = !prevEquipeOpen.current
    prevEquipeOpen.current = true
    if (justOpened || teamMembers.length === 0) {
      setTeamMembers(baseTeamList.map((m) => ({ ...m, ativo: false })))
    }
  }, [dialogEquipeAberto, baseTeamList, teamMembers.length])

  useEffect(() => {
    if (!dialogEquipeAberto || !baseTeamList.length || recipientsData === undefined) return
    if (hasInitializedEquipeModal.current) return
    hasInitializedEquipeModal.current = true
    const ids = (recipientsData?.inpiNotificationRecipients?.accountUserIds || []).map(String)
    setTeamMembers(baseTeamList.map((m) => ({ ...m, ativo: ids.includes(String(m.id)) })))
  }, [dialogEquipeAberto, baseTeamList, recipientsData])

  const [saveNotificationRecipients, { loading: savingRecipients }] = useMutation(
    inpiMutations.SAVE_INPI_NOTIFICATION_RECIPIENTS,
    {
      onCompleted: () => {
        if (typeof refetchRecipients === 'function') refetchRecipients()
        showMessage({
          title: 'Salvo',
          message: 'Destinatários de notificação atualizados.',
          color: 'success',
          position: 'bottom-left',
          time: 4000
        })
        setDialogEquipeAberto(false)
      },
      onError: () => {
        showMessage({
          title: 'Erro',
          message: 'Não foi possível salvar. Tente novamente.',
          color: 'danger',
          position: 'bottom-left',
          time: 4000
        })
      }
    }
  )

  const [addProcess, { loading: adding }] = useMutation(inpiMutations.ADD_INPI_PROCESS, {
    onCompleted: (result) => {
      if (!result || !result.addInpiProcess) return
      setAddModalOpen(false)
      setProcessNumber('')
      setProcessType('marca')
      setPreSelectedTipo(null)
      setAddError('')
      if (typeof refetch === 'function') {
        refetch()
      }
    },
    onError: ({ graphQLErrors }) => {
      setAddError(graphQLErrors[0].extensions.response.body.message || 'Erro ao adicionar processo')
    }
  })

  const handleCloseDetalhe = () => {
    setDialogDetalheAberto(false)
    setSelectedRegistro(null)
  }

  const toggleDetalheSection = (key) => {
    setDetalheSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }


  const registros = useMemo(() => {
    const processes = data?.inpiProcesses?.data || []
    return processes.map(mapProcessToRegistro)
  }, [data?.inpiProcesses?.data])

  const registrosFiltrados = useMemo(() => {
    return registros.filter((r) => {
      const matchBusca =
        !busca ||
        r.nome.toLowerCase().includes(busca.toLowerCase()) ||
        r.protocolo.toLowerCase().includes(busca.toLowerCase())
      const matchStatus = filtroStatus === 'todos' || r.status === filtroStatus
      const matchTipo = filtroTipo === 'todos' || r.tipo === filtroTipo || (filtroTipo === 'marca' && r.tipo === 'marca_internacional')
      return matchBusca && matchStatus && matchTipo
    })
  }, [registros, busca, filtroStatus, filtroTipo])

  const toggleMembro = (id) => {
    setTeamMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ativo: !m.ativo } : m))
    )
  }

  const savedRecipientsCount = recipientsData?.inpiNotificationRecipients?.accountUserIds?.length ?? 0
  const membrosAtivos = teamMembers.filter((m) => m.ativo).length
  const badgeCount = dialogEquipeAberto ? membrosAtivos : savedRecipientsCount
  const membrosFiltrados = teamMembers.filter(
    (m) =>
      !buscaEquipe ||
      m.nome.toLowerCase().includes(buscaEquipe.toLowerCase()) ||
      m.email.toLowerCase().includes(buscaEquipe.toLowerCase())
  )

  const openAddModal = (tipo) => {
    if (tipo === 'marca') {
      setDialogNovoAberto(false)
      setMarcaDropdownOpen(true)
      return
    }
    if (tipo === 'software') {
      setDialogNovoAberto(false)
      history.push('/dashboard/serviços/solicitar?subserviceId=653ad225b8c12a000881bccf')
      return
    }
    if (tipo === 'marca_internacional') {
      setDialogNovoAberto(false)
      history.push('/dashboard/serviços/solicitar?subserviceId=600829e049980a00080c4707')
      return
    }
    if (tipo === 'patente') {
      setDialogNovoAberto(false)
      history.push('/dashboard/serviços/solicitar?subserviceId=653c0684bef4b00008730599')
      return
    }
    setPreSelectedTipo(tipo)
    setProcessType(tipo || 'marca')
    setProcessNumber('')
    setAddError('')
    setDialogNovoAberto(false)
    setAddModalOpen(true)
  }

  const handleAddProcess = () => {
    const trimmed = (processNumber || '').trim()
    if (!trimmed) {
      setAddError('Informe o número do processo')
      return
    }
    setAddError('')
    addProcess({ variables: { processNumber: trimmed, processType } })
  }

  const navigationLinks = [
    { label: 'Início', onClick: () => history.push('/dashboard') },
    { label: 'Registros INPI' }
  ]

  return (
    <>
      <Flex width={1} justifyContent="center" flexDirection="column" className="service">
        <Flex width={1} mb={3} className="breadCrumb">
          <BreadCrumb ml={-3} color="primary" navigationLinks={navigationLinks} />
        </Flex>
      </Flex>
      <div className="registros-inpi-container">
      {/* Header INPI - sem botão Visão do Advogado */}
      <div className="registros-inpi-header">
        <div className="registros-inpi-header-inner">
          <div>
            <div className="registros-inpi-header-title">
              <Shield size={24} className="registros-inpi-header-icon" />
              <h1>Registros INPI</h1>
            </div>
            <p className="registros-inpi-subtitle">
              Acompanhe todos os seus pedidos de propriedade intelectual
            </p>
          </div>
          <div className="registros-inpi-header-actions">
            <button
              type="button"
              className="registros-inpi-btn-outline"
              onClick={() => setDialogEquipeAberto(true)}
            >
              <Bell size={15} />
              <span>Notificações da equipe</span>
              {badgeCount > 0 && (
                <span className="inpi-badge-count">{badgeCount}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Ações rápidas */}
      <section>
        <h2 className="registros-inpi-section-title">Solicitar novo registro</h2>
        <div className="registros-inpi-quick-actions">
          {ACOES_SOLICITACAO.map((acao) => {
            const Icon = acao.icon
            const config = tipoConfig[acao.tipo] || tipoConfig.outro
            if (acao.tipo === 'marca') {
              return (
                <Dropdown
                  key={acao.tipo}
                  show={marcaDropdownOpen}
                  onToggle={(open) => setMarcaDropdownOpen(open)}
                  className="registros-inpi-marca-dropdown"
                >
                  <Dropdown.Toggle
                    as="button"
                    type="button"
                    className="registros-inpi-action-card registros-inpi-action-card-dropdown"
                  >
                    <span className={`inpi-action-icon ${config.className}`}>
                      <Icon size={16} />
                    </span>
                    <div>
                      <p className="inpi-action-label">{acao.label}</p>
                      <p className="inpi-action-desc">{acao.descricao}</p>
                    </div>
                    <ChevronDown size={16} className="inpi-action-chevron" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="start" className="registros-inpi-marca-menu">
                    {OPCOES_REGISTRAR_MARCA.map((opcao) => (
                      <Dropdown.Item
                        key={opcao.subserviceId}
                        onClick={() => {
                          setMarcaDropdownOpen(false)
                          history.push(`/dashboard/serviços/solicitar?subserviceId=${opcao.subserviceId}`)
                        }}
                      >
                        {opcao.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )
            }
            return (
              <button
                key={acao.tipo}
                type="button"
                className="registros-inpi-action-card"
                onClick={() => openAddModal(acao.tipo)}
              >
                <span className={`inpi-action-icon ${config.className}`}>
                  <Icon size={16} />
                </span>
                <div>
                  <p className="inpi-action-label">{acao.label}</p>
                  <p className="inpi-action-desc">{acao.descricao}</p>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* Filtros */}
      <section className="registros-inpi-filters">
        <div className="registros-inpi-search-wrap">
          <input
            type="text"
            className="registros-inpi-search-input"
            placeholder="Buscar por nome ou protocolo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <span className="registros-inpi-search-icon-wrap">
            <Search size={18} className="inpi-search-icon" />
          </span>
        </div>
        <select
          className="registros-inpi-select"
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          className="registros-inpi-select"
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
        >
          {TIPO_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span className="registros-inpi-filter-count">
          {registrosFiltrados.length} registro{registrosFiltrados.length !== 1 ? 's' : ''}
        </span>
      </section>

      {/* Tabela */}
      <section className="registros-inpi-table-wrap">
        <div className="registros-inpi-table-header">
          <span>Registro</span>
          <span>Protocolo</span>
          <span>Tipo</span>
          <span>Nº Serviço</span>
          <span>Status</span>
          <span>Última movimentação</span>
          <span>&nbsp;</span>
        </div>

        {loading ? (
          <div className="registros-inpi-empty">
            <Spinner color="primary" />
            <p>Carregando registros...</p>
          </div>
        ) : registrosFiltrados.length === 0 ? (
          <div className="registros-inpi-empty">
            <FileText size={40} />
            <p>Nenhum registro encontrado</p>
            <p className="inpi-empty-hint">Tente ajustar os filtros ou adicione um novo pedido</p>
          </div>
        ) : (
          <div className="registros-inpi-table-rows">
            {registrosFiltrados.map((registro) => (
              <div
                key={registro.id}
                className="registros-inpi-table-row"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelectedRegistro(registro)
                  setDialogDetalheAberto(true)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedRegistro(registro)
                    setDialogDetalheAberto(true)
                  }
                }}
              >
                <div>
                  <p className="registros-inpi-row-nome">{registro.nome}</p>
                  <p className="registros-inpi-row-data">Aberto em {registro.dataAbertura}</p>
                </div>
                <div>
                  <code className="registros-inpi-row-protocolo">{registro.protocolo}</code>
                </div>
                <div>
                  <TipoIcon tipo={registro.tipo} showLabel />
                </div>
                <div className="registros-inpi-row-servico">
                  {registro.numeroServico ? (
                    <a href={`#servico/${registro.numeroServico}`} title="Abrir serviço" onClick={(e) => e.stopPropagation()}>
                      <ExternalLink size={11} />
                      {registro.numeroServico}
                    </a>
                  ) : (
                    <span className="inpi-dash">—</span>
                  )}
                </div>
                <div>
                  <StatusBadge status={registro.status} />
                </div>
                <div className="registros-inpi-row-movimentacao" onClick={(e) => e.stopPropagation()}>
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="top"
                    overlay={
                      <Popover className="inpi-popover" id={`popover-${registro.id}`}>
                        <Popover.Body>
                          <div className="inpi-popover-header">
                            <StatusBadge status={registro.status} />
                          </div>
                          <div className="inpi-popover-content">
                            <p>{statusConfig[registro.status]?.descricao || ''}</p>
                          </div>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <button type="button">
                      <Clock size={12} className="inpi-mov-icon" />
                      <span className="inpi-mov-text">{registro.ultimaMovimentacao}</span>
                    </button>
                  </OverlayTrigger>
                </div>
                <div className="registros-inpi-row-chevron">
                  <ChevronRight size={15} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modal: Novo pedido */}
      <Modal
        open={dialogNovoAberto}
        onClose={() => setDialogNovoAberto(false)}
        title="Novo pedido de registro"
      >
        <div className={isDark ? 'dark' : undefined}>
        <div className="registros-inpi-modal-novo">
          <p style={{ color: 'var(--inpi-muted)', fontSize: 14, marginBottom: 0 }}>
            Selecione o tipo de registro que deseja solicitar ao INPI.
          </p>
          <div className="inpi-modal-options">
            {ACOES_SOLICITACAO.map((acao) => {
              const Icon = acao.icon
              const config = tipoConfig[acao.tipo] || tipoConfig.outro
              return (
                <button
                  key={acao.tipo}
                  type="button"
                  className="registros-inpi-modal-option"
                  onClick={() => openAddModal(acao.tipo)}
                >
                  <span className={`inpi-modal-option-icon ${config.className}`}>
                    <Icon size={18} />
                  </span>
                  <div className="inpi-modal-option-text">
                    <p className="inpi-modal-option-label">{acao.label}</p>
                    <p className="inpi-modal-option-desc">{acao.descricao}</p>
                  </div>
                  <ChevronRight size={15} className="inpi-modal-option-chevron" />
                </button>
              )
            })}
            <button
              type="button"
              className="registros-inpi-modal-option inpi-modal-option-dashed"
              onClick={() => openAddModal(null)}
            >
              <span className="inpi-modal-option-icon inpi-modal-option-icon-muted">
                <Plus size={18} />
              </span>
              <div className="inpi-modal-option-text">
                <p className="inpi-modal-option-label">Já tenho acompanhamento</p>
                <p className="inpi-modal-option-desc">Cadastrar protocolo existente no INPI</p>
              </div>
              <ChevronRight size={15} className="inpi-modal-option-chevron" />
            </button>
          </div>
        </div>
        </div>
      </Modal>

      {/* Modal: Equipe */}
      <Modal
        open={dialogEquipeAberto}
        onClose={() => setDialogEquipeAberto(false)}
        title="Notificações da equipe"
      >
        <div className={isDark ? 'dark' : undefined}>
        <div className="registros-inpi-modal-equipe">
          <p style={{ color: 'var(--inpi-muted)', fontSize: 14 }}>
            Selecione quem deve receber as atualizações de movimentação do INPI.
          </p>
          <div className="inpi-equipe-search-wrap">
            <Search size={14} className="inpi-search-icon" />
            <input
              type="text"
              className="inpi-equipe-search-input"
              placeholder="Buscar na equipe..."
              value={buscaEquipe}
              onChange={(e) => setBuscaEquipe(e.target.value)}
            />
          </div>
          <div className="inpi-equipe-list">
            {membrosFiltrados.map((membro) => (
              <div key={membro.id} className="inpi-equipe-member">
                <div className="inpi-equipe-avatar">
                  {membro.nome
                    .split(' ')
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join('')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="inpi-equipe-member-name">{membro.nome}</p>
                  <p className="inpi-equipe-member-email">{membro.email}</p>
                </div>
                <button
                  type="button"
                  className={`inpi-equipe-toggle ${membro.ativo ? 'active' : ''}`}
                  onClick={() => toggleMembro(membro.id)}
                >
                  {membro.ativo && <Check size={12} />}
                </button>
              </div>
            ))}
          </div>
          <div className="inpi-equipe-link-wrap">
            <Users size={15} style={{ color: 'var(--inpi-muted)' }} />
            <p style={{ flex: 1, margin: 0, fontSize: 12, color: 'var(--inpi-muted)' }}>
              O usuário não está na lista?
            </p>
            <button
              type="button"
              className="inpi-equipe-link-btn"
              onClick={() => {
                setDialogEquipeAberto(false)
                history.push('/dashboard/minha-equipe')
              }}
            >
              Ir para Minha Equipe
              <ExternalLink size={11} style={{ marginLeft: 4 }} />
            </button>
          </div>
          <p className="inpi-equipe-footer">
            {membrosAtivos} membro{membrosAtivos !== 1 ? 's' : ''} receberá{membrosAtivos !== 1 ? 'ão' : ''} notificações
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button
              type="button"
              className="inpi-equipe-link-btn"
              onClick={() => setDialogEquipeAberto(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="inpi-add-btn-primary"
              style={{ minWidth: 100 }}
              disabled={savingRecipients}
              onClick={() => {
                const accountUserIds = teamMembers.filter((m) => m.ativo).map((m) => String(m.id))
                saveNotificationRecipients({ variables: { accountUserIds } })
              }}
            >
              {savingRecipients ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
        </div>
      </Modal>

      {/* Modal: Detalhe do registro */}
      <Modal
        open={dialogDetalheAberto}
        onClose={handleCloseDetalhe}
        title={selectedRegistro ? selectedRegistro.nome : 'Detalhe'}
      >
        {selectedRegistro && (
          <div className={isDark ? 'dark' : undefined} style={{ height: '100%' }}>
          <div className="registros-inpi-modal-detalhe">
            {(() => {
              const proc = processDetail?.inpiProcess
              const tipo = selectedRegistro.tipo || proc?.processType || 'marca'
              const isSoftware = tipo === 'software'
              const isPatente = tipo === 'patente'
              const rp = proc?.renewalPeriods
              const classes = proc?.classes || []
              const petitions = proc?.petitions || []
              const movements = proc?.movements || []
              const hasClasses = classes.length > 0
              const hasPetitions = petitions.length > 0
              const hasMovements = movements.length > 0

              /* ---------- Modal SOFTWARE (Programa de Computador) — dados e seções específicos; não alterar o return de marca abaixo ---------- */
              if (isSoftware) {
                const softwarePublicationDate = proc?.publicationDate || (movements[0] && movements[0].date)
                const campoAplicacaoItems = Array.isArray(proc?.campoAplicacao) ? proc.campoAplicacao : (proc?.campoAplicacao ? [proc.campoAplicacao] : [])
                const tipoProgramaItems = Array.isArray(proc?.tipoPrograma) ? proc.tipoPrograma : (proc?.tipoPrograma ? [proc.tipoPrograma] : [])
                return (
                  <div className="inpi-detalhe-scroll">
                    <div className="inpi-detalhe-header-row">
                      <TipoIcon tipo={selectedRegistro.tipo} />
                      <StatusBadge status={selectedRegistro.status} />
                      <code className="inpi-detalhe-value inpi-protocolo">{formatProcessNumberBR(selectedRegistro.protocolo) || selectedRegistro.protocolo}</code>
                    </div>

                    <div className="inpi-detalhe-section">
                      <h4 className="inpi-detalhe-section-title">Programa de Computador</h4>
                      <div className="inpi-detalhe-grid">
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Nº do Pedido</p>
                          <p className="inpi-detalhe-value">{formatProcessNumberBR(selectedRegistro.protocolo) || selectedRegistro.protocolo}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Título</p>
                          <p className="inpi-detalhe-value">{proc?.trademarkName || selectedRegistro.nome || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Data do Depósito</p>
                          <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Situação</p>
                          <p className="inpi-detalhe-value">{proc?.status || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field inpi-detalhe-field-full">
                          <p className="inpi-detalhe-label">Linguagem</p>
                          <p className="inpi-detalhe-value">{proc?.linguagem || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field inpi-detalhe-field-full">
                          <p className="inpi-detalhe-label">Campo de Aplicação</p>
                          <div className="inpi-detalhe-value inpi-lines-wrap">
                            {campoAplicacaoItems.length
                              ? campoAplicacaoItems.map((line, i) => (
                                  <div key={i} className="inpi-line-item">{line}</div>
                                ))
                              : '—'}
                          </div>
                        </div>
                        <div className="inpi-detalhe-field inpi-detalhe-field-full">
                          <p className="inpi-detalhe-label">Tipo Programa</p>
                          <div className="inpi-detalhe-value inpi-lines-wrap">
                            {tipoProgramaItems.length
                              ? tipoProgramaItems.map((line, i) => (
                                  <div key={i} className="inpi-line-item">{line}</div>
                                ))
                              : '—'}
                          </div>
                        </div>
                        <div className="inpi-detalhe-field inpi-detalhe-field-full">
                          <p className="inpi-detalhe-label">Despacho / Observações</p>
                          <p className="inpi-detalhe-value">{proc?.statusDetail || '—'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="inpi-detalhe-section">
                      <h4 className="inpi-detalhe-section-title">Titular, Autor e Procurador</h4>
                      <div className="inpi-detalhe-grid">
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Nome do Titular</p>
                          <p className="inpi-detalhe-value">
                            {(!proc?.applicant || /^Titular\(\d+\):?\s*$/i.test(proc.applicant)) ? '—' : proc.applicant}
                          </p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Nome do Autor</p>
                          <p className="inpi-detalhe-value">{proc?.autor || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Nome do Procurador</p>
                          <p className="inpi-detalhe-value">
                            {(!proc?.procurador || /^Procurador:?\s*$/i.test(proc.procurador)) ? '—' : proc.procurador}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="inpi-detalhe-section">
                      <h4 className="inpi-detalhe-section-title">Datas</h4>
                      <div className="inpi-detalhe-grid inpi-detalhe-grid-3">
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Data do Depósito</p>
                          <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Data de Publicação (RPI)</p>
                          <p className="inpi-detalhe-value">{formatDateBR(softwarePublicationDate) || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Última atualização</p>
                          <p className="inpi-detalhe-value">
                            {proc?.lastScrapedAt
                              ? formatDateBR(
                                typeof proc.lastScrapedAt === 'number'
                                  ? new Date(proc.lastScrapedAt).toISOString().slice(0, 10)
                                  : proc.lastScrapedAt
                              )
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {hasPetitions && (
                      <div className="inpi-detalhe-section">
                        <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection('peticoes')}>
                          <h4 className="inpi-detalhe-section-title">
                            <span className="inpi-section-icon"><FileText size={18} /></span>
                            Petições
                            <span className="inpi-section-badge">{petitions.length}</span>
                          </h4>
                          {detalheSections.peticoes ? <ChevronUp size={20} strokeWidth={2} /> : <ChevronDown size={20} strokeWidth={2} />}
                        </button>
                        {detalheSections.peticoes && (
                          <div className="inpi-detalhe-table-wrap">
                            <table className="inpi-detalhe-table">
                              <thead>
                                <tr>
                                  <th>Pagamento</th>
                                  <th>Protocolo</th>
                                  <th>Data</th>
                                  <th>Serviço</th>
                                  <th>Cliente</th>
                                </tr>
                              </thead>
                              <tbody>
                                {petitions.map((p, i) => (
                                  <tr key={i}>
                                    <td>
                                      {p.statusDescricao || p.status ? (
                                        <OverlayTrigger
                                          trigger={['hover', 'focus']}
                                          placement="top"
                                          overlay={
                                            <Popover className="inpi-popover" id={`petition-pgo-sw-${i}`}>
                                              <Popover.Body>
                                                <p className="inpi-servico-descricao-popover">{p.statusDescricao || p.status || '—'}</p>
                                              </Popover.Body>
                                            </Popover>
                                          }
                                        >
                                          <span className="inpi-servico-cell inpi-status-hoverable" title="Informações de pagamento">
                                            {p.status || (p.statusDescricao ? 'Ver pagamento' : '—')}
                                          </span>
                                        </OverlayTrigger>
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </td>
                                    <td><code className="inpi-table-code">{p.protocolo || '—'}</code></td>
                                    <td>{formatDateBR(p.data) || '—'}</td>
                                    <td>
                                      {p.servicoDescricao ? (
                                        <OverlayTrigger
                                          trigger={['hover', 'focus']}
                                          placement="top"
                                          overlay={
                                            <Popover className="inpi-popover" id={`petition-servico-sw-${i}`}>
                                              <Popover.Body>
                                                <p className="inpi-servico-descricao-popover">{p.servicoDescricao}</p>
                                              </Popover.Body>
                                            </Popover>
                                          }
                                        >
                                          <span className="inpi-servico-cell">
                                            <code className="inpi-table-code">{p.servico || '—'}</code>
                                          </span>
                                        </OverlayTrigger>
                                      ) : (
                                        <code className="inpi-table-code">{p.servico || '—'}</code>
                                      )}
                                    </td>
                                    <td>{p.cliente || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="inpi-detalhe-section inpi-publicacoes-section">
                      <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection('publicacoes')}>
                        <h4 className="inpi-detalhe-section-title">
                          <span className="inpi-section-icon"><List size={18} /></span>
                          Publicações
                          {hasMovements && <span className="inpi-section-badge">{movements.length}</span>}
                        </h4>
                        {detalheSections.publicacoes ? <ChevronUp size={20} strokeWidth={2} /> : <ChevronDown size={20} strokeWidth={2} />}
                      </button>
                      {detalheSections.publicacoes && (
                        <>
                          {loadingDetail ? (
                            <div className="inpi-publicacoes-loading">
                              <div className="inpi-loading-dots" />
                              <span>Carregando publicações...</span>
                            </div>
                          ) : hasMovements ? (
                            <div className="inpi-publicacoes-timeline">
                              <div className="inpi-timeline-track" />
                              {movements.map((mov, idx) => (
                                <article key={mov._id || idx} className={`inpi-publicacao-card ${idx === 0 ? 'inpi-publicacao-recente' : ''}`}>
                                  <div className="inpi-publicacao-marker">
                                    <span className="inpi-publicacao-dot" />
                                  </div>
                                  <div className="inpi-publicacao-body">
                                    <header className="inpi-publicacao-header">
                                      <div className="inpi-publicacao-meta">
                                        {idx === 0 && <span className="inpi-publicacao-recente-badge">Mais recente</span>}
                                        {(mov.rpi || mov.code) && (
                                          <span className="inpi-publicacao-rpi">RPI {mov.rpi || mov.code}</span>
                                        )}
                                        <time className="inpi-publicacao-data">{formatDateBR(mov.date) || '—'}</time>
                                      </div>
                                    </header>
                                    <p className="inpi-publicacao-descricao">{mov.description || '—'}</p>
                                    {mov.complemento && (
                                      <details className="inpi-publicacao-complemento">
                                        <summary>
                                          <span>Ver detalhes do despacho</span>
                                          <ChevronRight size={14} className="inpi-complemento-chevron" />
                                        </summary>
                                        <div className="inpi-publicacao-complemento-content">
                                          {formatComplemento(mov.complemento)
                                            .split('\n')
                                            .filter(Boolean)
                                            .map((line, i) => (
                                              <span key={i} className="inpi-complemento-line">{line}</span>
                                            ))}
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                </article>
                              ))}
                            </div>
                          ) : (
                            <div className="inpi-publicacoes-empty">
                              <List size={40} strokeWidth={1.5} />
                              <p>Nenhuma publicação registrada</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {proc?.lastUpdateInfo && (
                      <p className="inpi-detalhe-footer">{proc.lastUpdateInfo}</p>
                    )}
                  </div>
                )
              }

              /* ---------- Modal PATENTE — layout específico ---------- */
              if (isPatente) {
                const patentePublicationDate = proc?.publicationDate || (movements[0] && movements[0].date)
                return (
                  <div className="inpi-detalhe-scroll">
                    <div className="inpi-detalhe-header-row">
                      <TipoIcon tipo={selectedRegistro.tipo} />
                      <StatusBadge status={selectedRegistro.status} />
                      <code className="inpi-detalhe-value inpi-protocolo">{formatProcessNumberBR(selectedRegistro.protocolo) || selectedRegistro.protocolo}</code>
                    </div>

                    <div className="inpi-detalhe-section">
                      <h4 className="inpi-detalhe-section-title">Patente</h4>
                      <div className="inpi-detalhe-grid">
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Nº do Pedido</p>
                          <p className="inpi-detalhe-value">{formatProcessNumberBR(selectedRegistro.protocolo) || selectedRegistro.protocolo}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Título</p>
                          <p className="inpi-detalhe-value">{proc?.trademarkName || proc?.title || selectedRegistro.nome || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Data do Depósito</p>
                          <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Situação</p>
                          <p className="inpi-detalhe-value">{proc?.status || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field inpi-detalhe-field-full">
                          <p className="inpi-detalhe-label">Despacho / Observações</p>
                          <p className="inpi-detalhe-value">{proc?.statusDetail || '—'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="inpi-detalhe-section">
                      <h4 className="inpi-detalhe-section-title">Depositante e Procurador</h4>
                      <div className="inpi-detalhe-grid">
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Nome do Depositante</p>
                          <p className="inpi-detalhe-value">{proc?.applicant || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Nome do Procurador</p>
                          <p className="inpi-detalhe-value">{proc?.procurador || '—'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="inpi-detalhe-section">
                      <h4 className="inpi-detalhe-section-title">Datas</h4>
                      <div className="inpi-detalhe-grid inpi-detalhe-grid-3">
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Data do Depósito</p>
                          <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Data da Publicação</p>
                          <p className="inpi-detalhe-value">{formatDateBR(patentePublicationDate) || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Data da Concessão</p>
                          <p className="inpi-detalhe-value">{formatDateBR(proc?.grantDate) || '—'}</p>
                        </div>
                        <div className="inpi-detalhe-field">
                          <p className="inpi-detalhe-label">Última atualização</p>
                          <p className="inpi-detalhe-value">
                            {proc?.lastScrapedAt
                              ? formatDateBR(typeof proc.lastScrapedAt === 'number'
                                  ? new Date(proc.lastScrapedAt).toISOString().slice(0, 10)
                                  : String(proc.lastScrapedAt).slice(0, 10))
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {hasPetitions && (
                      <div className="inpi-detalhe-section">
                        <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection('peticoes')}>
                          <h4 className="inpi-detalhe-section-title">
                            <span className="inpi-section-icon"><FileText size={18} /></span>
                            Petições
                            <span className="inpi-section-badge">{petitions.length}</span>
                          </h4>
                          {detalheSections.peticoes ? <ChevronUp size={20} strokeWidth={2} /> : <ChevronDown size={20} strokeWidth={2} />}
                        </button>
                        {detalheSections.peticoes && (
                          <div className="inpi-detalhe-table-wrap">
                            <table className="inpi-detalhe-table">
                              <thead>
                                <tr>
                                  <th>Pagamento</th>
                                  <th>Protocolo</th>
                                  <th>Data</th>
                                  <th>Serviço</th>
                                  <th>Cliente</th>
                                </tr>
                              </thead>
                              <tbody>
                                {petitions.map((p, i) => (
                                  <tr key={i}>
                                    <td>
                                      {p.statusDescricao || p.status ? (
                                        <OverlayTrigger
                                          trigger={['hover', 'focus']}
                                          placement="top"
                                          overlay={
                                            <Popover className="inpi-popover" id={`petition-pgo-pat-${i}`}>
                                              <Popover.Body>
                                                <p className="inpi-servico-descricao-popover">{p.statusDescricao || p.status || '—'}</p>
                                              </Popover.Body>
                                            </Popover>
                                          }
                                        >
                                          <span className="inpi-servico-cell inpi-status-hoverable" title="Informações de pagamento">
                                            {p.status || (p.statusDescricao ? 'Ver pagamento' : '—')}
                                          </span>
                                        </OverlayTrigger>
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </td>
                                    <td><code className="inpi-table-code">{p.protocolo || '—'}</code></td>
                                    <td>{formatDateBR(p.data) || '—'}</td>
                                    <td>
                                      {p.servicoDescricao ? (
                                        <OverlayTrigger
                                          trigger={['hover', 'focus']}
                                          placement="top"
                                          overlay={
                                            <Popover className="inpi-popover" id={`petition-servico-pat-${i}`}>
                                              <Popover.Body>
                                                <p className="inpi-servico-descricao-popover">{p.servicoDescricao}</p>
                                              </Popover.Body>
                                            </Popover>
                                          }
                                        >
                                          <span className="inpi-servico-cell">
                                            <code className="inpi-table-code">{p.servico || '—'}</code>
                                          </span>
                                        </OverlayTrigger>
                                      ) : (
                                        <code className="inpi-table-code">{p.servico || '—'}</code>
                                      )}
                                    </td>
                                    <td>{p.cliente || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="inpi-detalhe-section inpi-publicacoes-section">
                      <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection('publicacoes')}>
                        <h4 className="inpi-detalhe-section-title">
                          <span className="inpi-section-icon"><List size={18} /></span>
                          Publicações
                          {hasMovements && <span className="inpi-section-badge">{movements.length}</span>}
                        </h4>
                        {detalheSections.publicacoes ? <ChevronUp size={20} strokeWidth={2} /> : <ChevronDown size={20} strokeWidth={2} />}
                      </button>
                      {detalheSections.publicacoes && (
                        <>
                          {loadingDetail ? (
                            <div className="inpi-publicacoes-loading">
                              <div className="inpi-loading-dots" />
                              <span>Carregando publicações...</span>
                            </div>
                          ) : hasMovements ? (
                            <div className="inpi-publicacoes-timeline">
                              <div className="inpi-timeline-track" />
                              {movements.map((mov, idx) => (
                                <article key={mov._id || idx} className={`inpi-publicacao-card ${idx === 0 ? 'inpi-publicacao-recente' : ''}`}>
                                  <div className="inpi-publicacao-marker">
                                    <span className="inpi-publicacao-dot" />
                                  </div>
                                  <div className="inpi-publicacao-body">
                                    <header className="inpi-publicacao-header">
                                      <div className="inpi-publicacao-meta">
                                        {idx === 0 && <span className="inpi-publicacao-recente-badge">Mais recente</span>}
                                        {(mov.rpi || mov.code) && (
                                          <span className="inpi-publicacao-rpi">RPI {mov.rpi || mov.code}</span>
                                        )}
                                        <time className="inpi-publicacao-data">{formatDateBR(mov.date) || '—'}</time>
                                      </div>
                                    </header>
                                    <p className="inpi-publicacao-descricao">{mov.description || '—'}</p>
                                    {mov.complemento && (
                                      <details className="inpi-publicacao-complemento">
                                        <summary>
                                          <span>Ver detalhes do despacho</span>
                                          <ChevronRight size={14} className="inpi-complemento-chevron" />
                                        </summary>
                                        <div className="inpi-publicacao-complemento-content">
                                          {formatComplemento(mov.complemento)
                                            .split('\n')
                                            .filter(Boolean)
                                            .map((line, i) => (
                                              <span key={i} className="inpi-complemento-line">{line}</span>
                                            ))}
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                </article>
                              ))}
                            </div>
                          ) : (
                            <div className="inpi-publicacoes-empty">
                              <List size={40} strokeWidth={1.5} />
                              <p>Nenhuma publicação registrada</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {proc?.lastUpdateInfo && (
                      <p className="inpi-detalhe-footer">{proc.lastUpdateInfo}</p>
                    )}
                  </div>
                )
              }

              /* ---------- Modal MARCA (e demais tipos) — layout original ---------- */
              return (
                <div className="inpi-detalhe-scroll">
                  <div className="inpi-detalhe-header-row">
                    <TipoIcon tipo={selectedRegistro.tipo} />
                    <StatusBadge status={selectedRegistro.status} />
                    <code className="inpi-detalhe-value inpi-protocolo">{selectedRegistro.protocolo}</code>
                  </div>

                  <div className="inpi-detalhe-section">
                    <h4 className="inpi-detalhe-section-title">Resumo</h4>
                    <div className="inpi-detalhe-grid">
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Nº do Processo</p>
                        <p className="inpi-detalhe-value">{selectedRegistro.protocolo}</p>
                      </div>
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Marca</p>
                        <p className="inpi-detalhe-value">{proc?.trademarkName || selectedRegistro.nome || '—'}</p>
                      </div>
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Situação</p>
                        <p className="inpi-detalhe-value">{proc?.status || '—'}</p>
                      </div>
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Apresentação</p>
                        <p className="inpi-detalhe-value">{proc?.apresentacao || '—'}</p>
                      </div>
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Natureza</p>
                        <p className="inpi-detalhe-value">{proc?.natureza || '—'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="inpi-detalhe-section">
                    <h4 className="inpi-detalhe-section-title">Titulares e Representante Legal</h4>
                    <div className="inpi-detalhe-grid">
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Titular</p>
                        <p className="inpi-detalhe-value">
                          {(!proc?.applicant || /^Titular\(\d+\):?\s*$/i.test(proc.applicant)) ? '—' : proc.applicant}
                        </p>
                      </div>
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Procurador</p>
                        <p className="inpi-detalhe-value">
                          {(!proc?.procurador || /^Procurador:?\s*$/i.test(proc.procurador)) ? '—' : proc.procurador}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="inpi-detalhe-section">
                    <h4 className="inpi-detalhe-section-title">Datas</h4>
                    <div className="inpi-detalhe-grid inpi-detalhe-grid-3">
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Data de Depósito</p>
                        <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || '—'}</p>
                      </div>
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Data de Concessão</p>
                        <p className="inpi-detalhe-value">{formatDateBR(proc?.grantDate) || formatDateBR(proc?.publicationDate) || '—'}</p>
                      </div>
                      <div className="inpi-detalhe-field">
                        <p className="inpi-detalhe-label">Data de Vigência</p>
                        <p className="inpi-detalhe-value">{formatDateBR(proc?.vigenciaDate) || '—'}</p>
                      </div>
                    </div>
                  </div>

                  {rp && (rp.ordinary?.start || rp.ordinary?.end || rp.extraordinary?.start || rp.extraordinary?.end) && (
                    <div className="inpi-detalhe-section">
                      <h4 className="inpi-detalhe-section-title">Prazos para prorrogação de registro</h4>
                      <div className="inpi-detalhe-table-wrap">
                        <table className="inpi-detalhe-table">
                          <thead>
                            <tr>
                              <th />
                              <th>Prazo Ordinário</th>
                              <th>Prazo Extraordinário</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Início</strong></td>
                              <td>{formatDateBR(rp.ordinary?.start) || '—'}</td>
                              <td>{formatDateBR(rp.extraordinary?.start) || '—'}</td>
                            </tr>
                            <tr>
                              <td><strong>Fim</strong></td>
                              <td>{formatDateBR(rp.ordinary?.end) || '—'}</td>
                              <td>{formatDateBR(rp.extraordinary?.end) || '—'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {hasClasses && (
                    <div className="inpi-detalhe-section">
                      <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection('classificacao')}>
                        <h4 className="inpi-detalhe-section-title">
                          <span className="inpi-section-icon"><FileText size={18} /></span>
                          Classificação de Produtos / Serviços
                          <span className="inpi-section-badge">{classes.length}</span>
                        </h4>
                        {detalheSections.classificacao ? <ChevronUp size={20} strokeWidth={2} /> : <ChevronDown size={20} strokeWidth={2} />}
                      </button>
                      {detalheSections.classificacao && (
                        <div className="inpi-detalhe-table-wrap">
                          <table className="inpi-detalhe-table">
                            <thead>
                              <tr>
                                <th>Classe de Nice</th>
                                <th>Situação da Classe</th>
                                <th>Especificação</th>
                              </tr>
                            </thead>
                            <tbody>
                              {classes.map((c, i) => (
                                <tr key={i}>
                                  <td>{c.classeNice || '—'}</td>
                                  <td>{c.situacao || '—'}</td>
                                  <td>
                                    {c.especificacao && c.especificacao.length > 80 ? (
                                      <OverlayTrigger
                                        trigger={['hover', 'focus']}
                                        placement="left"
                                        overlay={
                                          <Popover className="inpi-popover inpi-especificacao-popover" id={`classe-espec-${i}`}>
                                            <Popover.Body>
                                              <p className="inpi-especificacao-popover-text">{c.especificacao}</p>
                                            </Popover.Body>
                                          </Popover>
                                        }
                                      >
                                        <span className="inpi-especificacao-cell">
                                          {c.especificacao.substring(0, 80)}...
                                        </span>
                                      </OverlayTrigger>
                                    ) : (
                                      c.especificacao || '—'
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Petições */}
                  {hasPetitions && (
                    <div className="inpi-detalhe-section">
                      <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection('peticoes')}>
                        <h4 className="inpi-detalhe-section-title">
                          <span className="inpi-section-icon"><FileText size={18} /></span>
                          Petições
                          <span className="inpi-section-badge">{petitions.length}</span>
                        </h4>
                        {detalheSections.peticoes ? <ChevronUp size={20} strokeWidth={2} /> : <ChevronDown size={20} strokeWidth={2} />}
                      </button>
                      {detalheSections.peticoes && (
                        <div className="inpi-detalhe-table-wrap">
                          <table className="inpi-detalhe-table">
                            <thead>
                              <tr>
                                <th>Protocolo</th>
                                <th>Data</th>
                                <th>Serviço</th>
                                <th>Cliente</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {petitions.map((p, i) => (
                                <tr key={i}>
                                  <td><code className="inpi-table-code">{p.protocolo || '—'}</code></td>
                                  <td>{formatDateBR(p.data) || '—'}</td>
                                  <td>
                                    {p.servicoDescricao ? (
                                      <OverlayTrigger
                                        trigger={['hover', 'focus']}
                                        placement="top"
                                        overlay={
                                          <Popover className="inpi-popover" id={`petition-servico-${i}`}>
                                            <Popover.Body>
                                              <p className="inpi-servico-descricao-popover">{p.servicoDescricao}</p>
                                            </Popover.Body>
                                          </Popover>
                                        }
                                      >
                                        <span className="inpi-servico-cell">
                                          <code className="inpi-table-code">{p.servico || '—'}</code>
                                        </span>
                                      </OverlayTrigger>
                                    ) : (
                                      <code className="inpi-table-code">{p.servico || '—'}</code>
                                    )}
                                  </td>
                                  <td>{p.cliente || '—'}</td>
                                  <td>
                                    {p.statusDescricao ? (
                                      <OverlayTrigger
                                        trigger={['hover', 'focus']}
                                        placement="top"
                                        delay={{ show: 150, hide: 100 }}
                                        overlay={
                                          <Popover className="inpi-popover inpi-status-popover" id={`petition-status-${i}`}>
                                            <Popover.Body>
                                              <p className="inpi-servico-descricao-popover">{p.statusDescricao}</p>
                                            </Popover.Body>
                                          </Popover>
                                        }
                                      >
                                        <span className="inpi-servico-cell inpi-status-hoverable" title="Passe o mouse para ver detalhes do pagamento">
                                          {p.status || '—'}
                                        </span>
                                      </OverlayTrigger>
                                    ) : (
                                      p.status || '—'
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Publicações (movimentações) - Timeline */}
                  <div className="inpi-detalhe-section inpi-publicacoes-section">
                    <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection('publicacoes')}>
                      <h4 className="inpi-detalhe-section-title">
                        <span className="inpi-section-icon">
                          <List size={18} />
                        </span>
                        Publicações
                        {hasMovements && <span className="inpi-section-badge">{movements.length}</span>}
                      </h4>
                      {detalheSections.publicacoes ? <ChevronUp size={20} strokeWidth={2} /> : <ChevronDown size={20} strokeWidth={2} />}
                    </button>
                    {detalheSections.publicacoes && (
                      <>
                        {loadingDetail ? (
                          <div className="inpi-publicacoes-loading">
                            <div className="inpi-loading-dots" />
                            <span>Carregando publicações...</span>
                          </div>
                        ) : hasMovements ? (
                          <div className="inpi-publicacoes-timeline">
                            <div className="inpi-timeline-track" />
                            {movements.map((mov, idx) => (
                              <article key={mov._id || idx} className={`inpi-publicacao-card ${idx === 0 ? 'inpi-publicacao-recente' : ''}`}>
                                <div className="inpi-publicacao-marker">
                                  <span className="inpi-publicacao-dot" />
                                </div>
                                <div className="inpi-publicacao-body">
                                  <header className="inpi-publicacao-header">
                                    <div className="inpi-publicacao-meta">
                                      {idx === 0 && <span className="inpi-publicacao-recente-badge">Mais recente</span>}
                                      {(mov.rpi || mov.code) && (
                                        <span className="inpi-publicacao-rpi">RPI {mov.rpi || mov.code}</span>
                                      )}
                                      <time className="inpi-publicacao-data">{formatDateBR(mov.date) || '—'}</time>
                                    </div>
                                  </header>
                                  <p className="inpi-publicacao-descricao">{mov.description || '—'}</p>
                                  {mov.complemento && (
                                    <details className="inpi-publicacao-complemento">
                                      <summary>
                                        <span>Ver detalhes do despacho</span>
                                        <ChevronRight size={14} className="inpi-complemento-chevron" />
                                      </summary>
                                      <div className="inpi-publicacao-complemento-content">
                                        {formatComplemento(mov.complemento)
                                          .split('\n')
                                          .filter(Boolean)
                                          .map((line, i) => (
                                            <span key={i} className="inpi-complemento-line">{line}</span>
                                          ))}
                                      </div>
                                    </details>
                                  )}
                                </div>
                              </article>
                            ))}
                          </div>
                        ) : (
                          <div className="inpi-publicacoes-empty">
                            <List size={40} strokeWidth={1.5} />
                            <p>Nenhuma publicação registrada</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Rodapé */}
                  {proc?.lastUpdateInfo && (
                    <p className="inpi-detalhe-footer">{proc.lastUpdateInfo}</p>
                  )}
                </div>
              )
            })()}
            <div className="inpi-detalhe-actions">
              <button type="button" className="inpi-detalhe-btn-fechar" onClick={handleCloseDetalhe}>
                Fechar
              </button>
            </div>
          </div>
          </div>
        )}
      </Modal>

      {/* Modal: Adicionar processo */}
      <Modal
        open={addModalOpen}
        onClose={() => {
          setAddModalOpen(false)
          setProcessNumber('')
          setProcessType('marca')
          setPreSelectedTipo(null)
          setAddError('')
        }}
        title="Adicionar processo INPI"
      >
        <div className={isDark ? 'dark' : undefined}>
        <div className="registros-inpi-modal-add">
          <p style={{ color: 'var(--inpi-muted)', fontSize: 14, marginBottom: 16 }}>
            {preSelectedTipo
              ? `Informe o número do processo de ${PROCESS_TYPES.find((t) => t.value === processType)?.label || processType}.`
              : 'Selecione o tipo e informe o número do processo que deseja acompanhar.'}
          </p>
          {!preSelectedTipo && (
            <div className="inpi-field">
              <label>Tipo de processo</label>
              <select
                value={processType}
                onChange={(e) => setProcessType(e.target.value)}
                className="inpi-select"
              >
                {PROCESS_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="inpi-field">
            <TextField
              label={
                processType === 'software' || processType === 'patente'
                  ? 'Número do Pedido'
                  : 'Número do processo'
              }
              value={processNumber}
              onChange={(e) => setProcessNumber(e.target.value)}
              placeholder={
                processType === 'software'
                  ? 'Ex: 512026000779-8 ou BR 51 2026 000779 8'
                  : processType === 'patente'
                    ? 'Ex: BR 10 2025 002123 4'
                    : 'Ex: BR 50 2024 001234'
              }
              fullWidth
            />
          </div>
          {addError && <p className="inpi-error">{addError}</p>}
          <div className="inpi-add-actions">
            <button
              type="button"
              className="inpi-add-btn-secondary"
              onClick={() => setAddModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="inpi-add-btn-primary"
              onClick={handleAddProcess}
              disabled={adding}
            >
              {adding ? 'Adicionando...' : 'Adicionar'}
            </button>
          </div>
        </div>
        </div>
      </Modal>
    </div>
    </>
  )
}

export default RegistrosInpi
